import * as d3 from 'd3';

// Helper constants
const classMap = {
  conn: 'radacct_conn',
  reco: 'radacct_reco',
  diss: 'radacct_diss',
  unkn: 'radacct_empty' // no info
};

const margin = 50;
const fontSize = 10;
const tickLen = 5;
const totalSlots = 24 * 4; // 96 quarter-hour slots

// Utility functions for rendering the graph
export const renderGraph = (node, width, height, payload, data) => {
  const heightOfItem = parseInt((height - margin * 2) / totalSlots);
  const vertStart = 15; 
  const vertEnd = vertStart + heightOfItem * totalSlots;
  const widthOfItem = (width - margin * 2) / payload.length;

  const radacctGraph = createGraph(node, width, height);

  renderGraphOutline(radacctGraph, width, height, widthOfItem, payload.length);

  payload.forEach((day, idx) => {
    renderDay(radacctGraph, day, idx, widthOfItem, heightOfItem, vertStart, vertEnd, totalSlots);
  });

  for (let i = 0; i <= totalSlots; i++) {
    renderYAxis(radacctGraph, i, vertStart, heightOfItem, totalSlots);
  }

  // Add the legend
  renderLegend(radacctGraph, width, height);

  return (
    <div className="graph" style={{ width: width, height: height }}>
      {node.toReact()}
    </div>
  );
};

const createGraph = (node, width, height) => {
  return d3.select(node)
    .append('svg')
    .attr('width', width)
    .attr('height', height);
};

const renderGraphOutline = (radacctGraph, width, height, widthOfItem, payloadLength) => {
  // Background rectangle
  radacctGraph.append('rect')
    .attr('x', 1)
    .attr('y', 1)
    .attr('width', width - 2)
    .attr('height', height - 2)
    .style('fill', 'rgb(255, 255, 255)');

  // Graph outline lines
  renderLineFromPoint(radacctGraph, { x: margin, y: 10 }, { x: margin, y: height - 40 });
  const right = margin + widthOfItem * payloadLength;
  renderLineFromPoint(radacctGraph, { x: right, y: 10 }, { x: right, y: height - 40 });

  // Axis label
  radacctGraph
    .append('text')
    .attr('text-anchor', 'start')
    .attr('class', 'raddact_legend')
    .attr('transform', `translate(${fontSize}, 275)rotate(270)`)
    .text('Time');
};

const renderLineFromPoint = (radacctGraph, start, end) => {
  radacctGraph.append('line')
    .attr('x1', start.x)
    .attr('y1', start.y)
    .attr('x2', end.x)
    .attr('y2', end.y)
    .style('stroke', 'rgb(0,0,0)')
    .style('stroke-width', 0.5);
};

const renderDay = (radacctGraph, day, idx, widthOfItem, heightOfItem, vertStart, vertEnd, totalSlots) => {
  const x = margin + idx * widthOfItem;

  // Create a map of all time slots
  const slotMap = Array(totalSlots).fill({ v: 'unkn' }); // default to grey

  // Fill the slots with actual data
  day.data.forEach(item => {
    const startTime = new Date(item.s);
    const hours = startTime.getHours();
    const minutes = startTime.getMinutes();
    const slot = (hours * 4) + Math.floor(minutes / 15);
    
    if (slot < totalSlots) {
      slotMap[slot] = item;
    }
  });

  // Day label
  radacctGraph
    .append('text')
    .attr('font-size', fontSize)
    .attr('font-family', 'arial')
    .attr('text-anchor', 'middle')
    .attr('alignment-baseline', 'middle')
    .attr(
      'transform',
      `translate(${x + (widthOfItem * 0.75) / 2},${vertEnd + tickLen + 25})rotate(300)`
    )
    .text(day.date);

  // Day vertical line
  renderLineFromPoint(radacctGraph, { x, y: vertEnd }, { x, y: vertEnd + tickLen });

  // Render all slots
  for (let slot = 0; slot < totalSlots; slot++) {
    const y = vertStart + (totalSlots - slot - 1) * heightOfItem;
    const item = slotMap[slot];
    renderDataRect(radacctGraph, { x, y }, item, widthOfItem, heightOfItem);
  }

  // Render y-axis (time labels)
  for (let i = 0; i <= totalSlots; i++) {
    if (i % 4 === 0) {
      const y = vertStart + (totalSlots - i - 1) * heightOfItem;
      const hourLabel = Math.floor(i / 4);
      
      radacctGraph.append('text')
        .attr('x', margin - tickLen - 5)
        .attr('y', y)
        .attr('font-size', fontSize)
        .attr('font-family', 'arial')
        .attr('text-anchor', 'end')
        .text(hourLabel);
      
      renderLineFromPoint(
        radacctGraph,
        { x: margin - tickLen, y },
        { x: margin, y }
      );
    }
  }
};

const renderDataRect = (radacctGraph, point, status, widthOfItem, heightOfItem) => {
  radacctGraph
    .append('rect')
    .attr('x', point.x)
    .attr('y', point.y)
    .attr('width', widthOfItem - 1)
    .attr('height', heightOfItem - 1)
    .attr('class', status.className || classMap[status.v] || 'radacct_empty');
};

const renderYAxis = (radacctGraph, i, vertStart, heightOfItem, totalSlots) => {
  let x1 = margin;
  const y = vertStart + (totalSlots - i - 1) * heightOfItem;

  if (i % 4 === 0) {
    x1 -= tickLen;

    const hourLabel = i / 4;

    renderAxisLabelFromPoint(radacctGraph, { x: x1 - tickLen, y }, hourLabel);
    renderLineFromPoint(radacctGraph, { x: x1, y }, { x: x1 + tickLen, y });
  }
};

const renderAxisLabelFromPoint = (radacctGraph, point, text) => {
  radacctGraph.append('text')
    .attr('x', point.x)
    .attr('y', point.y)
    .attr('font-size', fontSize)
    .attr('font-family', 'arial')
    .attr('text-anchor', 'end')
    .text(text);
};

const renderLegend = (radacctGraph, width, height) => {
  const legendData = [
    { label: 'Connected during the 15 mins', class: 'radacct_conn' },
    { label: 'One or more drops', class: 'radacct_reco' },
    { label: 'Down for the whole 15 mins', class: 'radacct_diss' },
    { label: 'No Information', class: 'radacct_empty' }
  ];

  const legend = radacctGraph.append('g')
    .attr('class', 'legend')
    .attr('transform', `translate(${margin}, ${height - margin + 20})`);

  legendData.forEach((d, i) => {
    const g = legend.append('g').attr('transform', `translate(${i * 200}, 0)`);

    g.append('rect')
      .attr('width', 15)
      .attr('height', 15)
      .attr('class', d.class);

    g.append('text')
      .attr('x', 20)
      .attr('y', 12)
      .attr('font-size', fontSize)
      .attr('font-family', 'arial')
      .text(d.label);
  });
};
